import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { SystemService } from "./system.service";
import { catchError, tap } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
  providedIn: "root",
})
export class FeedbackService {
  private feedbackCache: { [key: number]: any } = {}; // Cache for individual feedback sessions
  private employeeFeedbackCache: any[] = [];
  private consolidatedFeedbackCache: any;
  private allFeedbackCache: any[] = []; // Cache for all feedback sessions

  constructor(private http: HttpClient, private systemService: SystemService, private translate: TranslateService) {}

  // Submit feedback
  submitFeedback(data: any): Observable<any> {
    return this.http.post(
      this.systemService.apiURL + "/common/feedbacks",
      data
    );
  }

  getEmployeeFeedbacks(): Observable<any[]> {
    // Check if the cache is not empty
    if (this.employeeFeedbackCache.length > 0) {
      return of(this.employeeFeedbackCache); // Return cached data
    }

    return this.http
      .get<any[]>(this.systemService.apiURL + "/common/feedbacks")
      .pipe(
        tap((data) => {
          this.employeeFeedbackCache = data; // Store the fetched data in cache
        }),
        catchError(this.handleError<any[]>("getEmployeeFeedbacks", [])) // Handle errors
      );
  }

  getConsolidatedFeedbacks(): Observable<any> {
    if (this.consolidatedFeedbackCache) {
      return of(this.consolidatedFeedbackCache); // Return cached data
    }

    return this.http
      .get<any[]>(this.systemService.apiURL + "/common/feedbacks/consolidated")
      .pipe(
        tap((data) => {
          this.consolidatedFeedbackCache = data; // Store the fetched data in cache
        }),
        catchError(this.handleError<any[]>("getConsolidatedFeedbacks", [])) // Handle errors
      );
  }


  processConsolidation(): Observable<any> {
    return this.http.get<any[]>(this.systemService.apiURL + "/common/feedbacks/process-consolidation");
  }

  // Get all feedback sessions with caching
  getFeedbacks(): Observable<any[]> {
    // Check if the cache is not empty
    if (this.allFeedbackCache.length > 0) {
      return of(this.allFeedbackCache); // Return cached data
    }

    // Fetch from API if not cached
    return this.http
      .get<any[]>(this.systemService.apiURL + "/feedback-sessions")
      .pipe(
        tap((data) => {
          this.allFeedbackCache = data; // Store the fetched data in cache
        }),
        catchError(this.handleError<any[]>("getFeedbacks", [])) // Handle errors
      );
  }

  // Get a single feedback session with caching
  getFeedback(id: number): Observable<any> {
    // Check if the feedback session is already cached
    if (this.feedbackCache[id]) {
      return of(this.feedbackCache[id]); // Return cached data
    }

    // Fetch from API if not cached
    return this.http
      .get<any>(`${this.systemService.apiURL + "/feedback-sessions"}/${id}`)
      .pipe(
        tap((data) => {
          this.feedbackCache[id] = data; // Store the fetched data in cache
        }),
        catchError(this.handleError<any>("getFeedback", null)) // Handle errors
      );
  }

  // Create a feedback session
  createFeedback(feedback: any): Observable<any> {
    return this.http
      .post(this.systemService.apiURL + "/feedback-sessions", feedback)
      .pipe(
        tap(() => {
          // Invalidate cache when a new feedback session is created
          this.allFeedbackCache = []; // Clear all feedback cache
        }),
        catchError(this.handleError<any>("createFeedback"))
      );
  }

  // Update a feedback session
  updateFeedback(id: number, feedback: any): Observable<any> {
    return this.http
      .put(
        `${this.systemService.apiURL + "/feedback-sessions"}/${id}`,
        feedback
      )
      .pipe(
        tap(() => {
          // Invalidate the specific feedback cache
          delete this.feedbackCache[id]; // Remove the updated feedback from cache
          this.allFeedbackCache = []; // Clear all feedback cache to refresh
        }),
        catchError(this.handleError<any>("updateFeedback"))
      );
  }

  // Delete a feedback session
  deleteFeedback(id: number): Observable<any> {
    return this.http
      .delete(`${this.systemService.apiURL + "/feedback-sessions"}/${id}`)
      .pipe(
        tap(() => {
          // Invalidate cache on deletion
          delete this.feedbackCache[id]; // Remove the deleted feedback from cache
          this.allFeedbackCache = []; // Clear all feedback cache to refresh
        }),
        catchError(this.handleError<any>("deleteFeedback"))
      );
  }

  // Create a feedback session details
  saveSessionDetails(id: number, details: any): Observable<any> {
    details.lang = this.translate.getDefaultLang();
    return this.http
      .post(
        `${this.systemService.apiURL}/feedback-sessions/${id}/details`,
        details
      )
      .pipe(
        tap((updatedFeedback) => {
          // Update the feedback cache with the new details
          this.feedbackCache[id] = updatedFeedback; // Merge updated details with existing cached feedback
        }),
        catchError(this.handleError<any>("saveSessionDetails"))
      );
  }

  // Handle HTTP operation that failed
  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed: ${error.message}`);
      return of(result as T); // Let the app keep running by returning an empty result.
    };
  }
}
